import { useState, useEffect } from "react";
import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import color from "../../Constants/colors";
import {
  useSingleSkill,
  useCreateSkill,
  useUpdateSkill,
} from "../../Query/useSkill";
import { SkillPayload } from "../../Models/skill";
import Loading from "../Common/Loading";
import { useWorkspaceColors } from "../../Contexts/WorkspaceColorContext";
import { ComoButton } from "../Common/ComoButton";
import { ComoInput } from "../Common/ComoInput";
import { useTranslation } from "react-i18next";

const SkillForm = () => {
  const { skillId } = useParams<{ skillId: string }>();
  const isEdit = !!skillId;
  const navigate = useNavigate();
  const { colors: workspace } = useWorkspaceColors();
  const { t } = useTranslation("skillForm");

  const { data: existingSkill, isLoading } = useSingleSkill(
    Number(skillId) || 0,
  );
  const createMutation = useCreateSkill();
  const updateMutation = useUpdateSkill();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [content, setContent] = useState("");

  useEffect(() => {
    if (existingSkill && isEdit) {
      setName(existingSkill.name);
      setDescription(existingSkill.description);
      setContent(existingSkill.content);
    }
  }, [existingSkill, isEdit]);

  const handleSubmit = () => {
    const payload: SkillPayload = { name, description, content };
    if (isEdit) {
      updateMutation.mutate(
        { skillId: Number(skillId), data: payload },
        { onSuccess: () => navigate("/skill/") },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: () => navigate("/skill/"),
      });
    }
  };

  if (isEdit && isLoading) {
    return (
      <Loading
        backgroundColor={color.gray100}
        color1={workspace.accent}
        color2={workspace.primary_dark}
      />
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: color.gray50 }}>
      <Box sx={{ maxWidth: "1024px", mx: "auto", px: { xs: 2, sm: 3 }, py: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <IconButton
              onClick={() => navigate("/skill/")}
              size="small"
              sx={{ color: workspace.primary_dark }}
            >
              <ArrowBackIcon />
            </IconButton>
            <LightbulbOutlinedIcon
              sx={{ fontSize: 32, color: workspace.primary_dark }}
            />
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: workspace.primary_dark }}
            >
              {isEdit ? t("editSkill") : t("createSkill")}
            </Typography>
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            borderLeft: `4px solid ${workspace.primary_dark}`,
            background: color.white,
            overflow: "visible",
          }}
        >
          <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("name")}
              </Typography>
              <ComoInput
                placeholder={t("namePlaceholder")}
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("description")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ mb: 1, display: "block", color: color.gray500 }}
              >
                {t("descriptionHint")}
              </Typography>
              <ComoInput
                placeholder={t("descriptionPlaceholder")}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
              />
            </Box>

            <Box>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, color: color.primary_dark }}
              >
                {t("content")}
              </Typography>
              <Typography
                variant="caption"
                sx={{ mb: 1, display: "block", color: color.gray500 }}
              >
                {t("contentHint")}
              </Typography>
              <ComoInput
                placeholder={t("contentPlaceholder")}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                multiline
                rows={18}
                sx={{
                  "& textarea": {
                    resize: "vertical",
                    overflow: "auto",
                  },
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              p: 3,
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              borderTop: `1px solid ${color.gray200}`,
            }}
          >
            <ComoButton variant="outlined" onClick={() => navigate("/skill/")}>
              {t("cancel")}
            </ComoButton>
            <ComoButton
              onClick={handleSubmit}
              disabled={
                !name.trim() ||
                createMutation.isPending ||
                updateMutation.isPending
              }
            >
              {isEdit ? t("update") : t("create")}
            </ComoButton>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default SkillForm;
